'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

import { AnimatedCounter } from '@/components/animated-counter';
import { ProjectCard } from '@/components/project-card';
import { Button } from '@/components/ui/button';
import { PROJECT_FILTERS } from '@/constants';
import { projects } from '@/data/projects';
import { ProjectCategory } from '@/types/project';

export function PortfolioShowcase() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const featuredFlag = searchParams.get('featured') === 'true';
  const initialFilter = searchParams.get('tech') as
    | ProjectCategory
    | 'all'
    | null;

  const activeFilter =
    initialFilter && PROJECT_FILTERS.some((f) => f.value === initialFilter)
      ? initialFilter
      : 'all';

  const filteredProjects = useMemo(() => {
    let base = projects;
    if (featuredFlag) {
      base = base.filter((p) => p.featured);
    }

    if (activeFilter !== 'all') {
      base = base.filter((project) => project.category === activeFilter);
    }

    return base;
  }, [activeFilter, featuredFlag]);

  const featuredProjects = useMemo(
    () => projects.filter((p) => p.featured),
    []
  );
  const totalProjects = projects.length;
  const currentYear = new Date().getFullYear();

  return (
    <section id="projects" className="py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold">Featured Projects</h2>
          <p className="text-muted-foreground mx-auto max-w-2xl">
            Here are some of my recent projects that showcase my skills and
            experience
          </p>
        </div>

        {/* Stats */}
        <div className="mb-12 grid gap-8 md:grid-cols-3">
          <div className="text-center">
            <div className="text-primary mb-2 text-4xl font-bold">
              <AnimatedCounter end={totalProjects} />
            </div>
            <p className="text-muted-foreground">Total Projects</p>
          </div>
          <div className="text-center">
            <div className="text-primary mb-2 text-4xl font-bold">
              <AnimatedCounter end={featuredProjects.length} />
            </div>
            <p className="text-muted-foreground">Featured</p>
          </div>
          <div className="text-center">
            <div className="text-primary mb-2 text-4xl font-bold">
              <AnimatedCounter end={currentYear} />
            </div>
            <p className="text-muted-foreground">Latest Year</p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-8 flex flex-wrap justify-center gap-2">
          {PROJECT_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              className="hover:cursor-pointer"
              variant={activeFilter === filter.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                const url = new URL(window.location.href);
                if (filter.value === 'all') {
                  url.searchParams.delete('tech');
                } else {
                  url.searchParams.set('tech', filter.value);
                }
                window.history.replaceState(null, '', url.toString());
              }}
            >
              {filter.label}
            </Button>
          ))}

          <Button
            key="featured"
            className="hover:cursor-pointer"
            variant={featuredFlag ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              const params = new URLSearchParams(searchParams.toString());
              params.set('featured', featuredFlag ? 'false' : 'true');
              router.push(`?${params.toString()}#projects`);
            }}
          >
            Featured
          </Button>
        </div>

        {/* Projects */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project) => (
            <ProjectCard key={project.id} {...project} />
          ))}
        </div>
      </div>
    </section>
  );
}
